import React, { useEffect, useState } from "react";
import {
	CCButton,
	CCDateField,
	CCField,
	CCModal,
	CCSelect,
} from "@pymblecitizencoders/cc-ui";
import { ExternalLink, LucideIcon, TriangleAlert } from "lucide-react";
import {
	EventRequirement,
	EventRequirementAnswer,
	RequirementField,
} from "../../types/event";
import {
	RequirementDraft,
	answersToDraft,
	draftErrors,
	draftToAnswers,
	joinDateTime,
	pruneHidden,
	splitDateTime,
	visibleFields,
} from "../../utils/requirementForms";
import "./eventManager.css";

/** What the modal hands back. Exactly one of the two is meaningful. */
export interface RequirementDetails {
	note: string;
	answers: EventRequirementAnswer[];
}

interface EventRequirementModalProps {
	/** The requirement being detailed. `null` closes the modal. */
	requirement: EventRequirement | null;
	/** Resolved by the tab, which owns the glyph map. */
	icon: LucideIcon;
	/** The note already on the event, or "" for a new one. */
	note: string;
	/** The answers already on the event, for a requirement with a form. */
	answers: EventRequirementAnswer[] | null | undefined;
	onSave: (details: RequirementDetails) => void;
	onClose: () => void;
}

/**
 * The detail behind one requirement — opened when it is ticked, and again from
 * the card afterwards.
 *
 * Ticking "Catering" says an event needs catering; it does not say for how
 * many, by when, or whether anyone is vegetarian. The teams who receive these
 * need that, so the modal is offered at the moment of ticking rather than left
 * behind an affordance most people would never find.
 *
 * TWO SHAPES. A requirement whose dictionary entry carries `fields` gets that
 * form: the questions the team already asks on its own intake form, so the
 * organiser does not answer them twice. Everything else gets the single note.
 *
 * Neither is compulsory to OPEN. Closing leaves the requirement ticked, and for
 * a form with required questions the card then says "Details needed" — the
 * honest state, and visible, rather than a modal that will not let go. What
 * the form does refuse is SAVING with required questions blank: a saved form
 * is what the team will read as complete.
 */
export const EventRequirementModal: React.FC<EventRequirementModalProps> = ({
	requirement,
	icon: Icon,
	note,
	answers,
	onSave,
	onClose,
}) => {
	const [draft, setDraft] = useState(note);
	const [formDraft, setFormDraft] = useState<RequirementDraft>({});
	/** Errors are shown only after a save is attempted, not while typing. */
	const [tried, setTried] = useState(false);

	/* Reseed when a different requirement is opened. The modal is mounted for
	   the life of the tab, so without this the previous card's detail would be
	   sitting in the boxes. */
	useEffect(() => {
		setDraft(note);
		setFormDraft(answersToDraft(answers));
		setTried(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- reseed on open, not on every parent render
	}, [requirement?.id]);

	if (!requirement) return null;

	const fields = requirement.fields;
	const hasForm = fields.length > 0;
	const shownFields = visibleFields(fields, formDraft);
	const errors = hasForm && tried ? draftErrors(fields, formDraft) : {};

	const setValue = (id: string, value: string) =>
		// Changing an answer can hide a dependent field; its answer goes with it.
		setFormDraft((prev) => pruneHidden(fields, { ...prev, [id]: value }));

	const save = () => {
		if (!hasForm) {
			onSave({ note: draft.trim(), answers: [] });
			return;
		}
		if (Object.keys(draftErrors(fields, formDraft)).length > 0) {
			setTried(true);
			return;
		}
		onSave({ note: "", answers: draftToAnswers(fields, formDraft) });
	};

	return (
		<CCModal
			open
			onClose={onClose}
			title={`${hasForm ? "Details" : "Note"} for ${requirement.label}`}
			className={`em-req-modal${hasForm ? " em-req-modal--form" : ""}`}
			footer={
				/* Cancel left, the way forward right — the same split as the create
				   flow, so the footers do not disagree with each other. */
				<div className="em-modal-footer">
					<CCButton variant="outline" onClick={onClose}>
						Cancel
					</CCButton>
					<CCButton variant="solid" colour="primary" onClick={save}>
						{hasForm ? "Save details" : "Save note"}
					</CCButton>
				</div>
			}
		>
			{/* Same hidden-h2 arrangement as EventModalHead: CCModal types `title`
			    as a string and points aria-labelledby at it, so the icon row is
			    built here and the real heading is hidden visually. The tile keeps
			    the requirement's own colour, so the modal is visibly about the card
			    that was just ticked. */}
			<div className="em-modal-head">
				<span
					className="em-modal-head__icon em-req-modal__icon"
					style={
						requirement.colour
							? { backgroundColor: requirement.colour }
							: undefined
					}
					aria-hidden="true"
				>
					<Icon size={24} />
				</span>
				<span className="em-modal-head__title" aria-hidden="true">
					{requirement.label}
				</span>
			</div>

			<p className="em-muted em-mb-md">{requirement.description}</p>

			{hasForm ? (
				<div className="em-req-form">
					{shownFields.map((field, index) => (
						<RequirementFieldControl
							key={field.id}
							field={field}
							value={formDraft[field.id] ?? ""}
							error={errors[field.id]}
							autoFocus={index === shownFields.findIndex((f) => f.type !== "info")}
							onChange={(value) => setValue(field.id, value)}
						/>
					))}
				</div>
			) : (
				<CCField
					id="em-req-note"
					label="Note"
					multiline
					rows={4}
					fullWidth
					autoFocus
					hint="Anything the team arranging this needs to know. Optional."
					value={draft}
					onChange={(e) => setDraft(e.target.value)}
				/>
			)}
		</CCModal>
	);
};

interface RequirementFieldControlProps {
	field: RequirementField;
	value: string;
	error?: string;
	autoFocus: boolean;
	onChange: (value: string) => void;
}

/** One question on the form, or one piece of guidance between them. */
const RequirementFieldControl: React.FC<RequirementFieldControlProps> = ({
	field,
	value,
	error,
	autoFocus,
	onChange,
}) => {
	const id = `em-req-field-${field.id}`;
	/* cc-ui controls take a plain `label`, so "required" is carried in the text.
	   An asterisk alone is a symbol screen readers announce as "star". */
	const label = field.required ? `${field.label} (required)` : field.label;

	switch (field.type) {
		case "info":
			return (
				<div
					className={`em-req-info${field.tone === "warning" ? " em-req-info--warning" : ""}`}
					role={field.tone === "warning" ? "note" : undefined}
				>
					{field.tone === "warning" && (
						<TriangleAlert size={16} aria-hidden="true" className="em-req-info__icon" />
					)}
					<div>
						{field.label && <p className="em-req-info__title">{field.label}</p>}
						{field.text && <p className="em-req-info__text">{field.text}</p>}
						{field.url && (
							<a
								className="em-req-info__link"
								href={field.url}
								target="_blank"
								rel="noreferrer noopener"
							>
								{field.linkLabel || field.url}
								<ExternalLink size={12} aria-hidden="true" />
							</a>
						)}
					</div>
				</div>
			);

		case "select":
			return (
				<CCSelect
					id={id}
					label={label}
					fullWidth
					placeholder="Choose..."
					options={field.options.map((o) => ({ value: o, label: o }))}
					value={value}
					hint={field.hint || undefined}
					error={error}
					autoFocus={autoFocus}
					onChange={(e) => onChange(e.target.value)}
				/>
			);

		case "datetime": {
			/* Two controls, as in the sidebar: cc-ui has no single datetime
			   input. CCDateField brands its values as IsoDate; the app carries
			   plain `YYYY-MM-DD`, so the cast stays at this boundary. */
			const { date, time } = splitDateTime(value);
			return (
				<fieldset className="em-req-datetime">
					<legend className="em-req-datetime__legend">{label}</legend>
					{field.hint && <p className="em-muted em-req-datetime__hint">{field.hint}</p>}
					<div className="em-req-datetime__row">
						<CCDateField
							id={`${id}-date`}
							label="Date"
							fullWidth
							value={(date || undefined) as never}
							onChange={(next) => onChange(joinDateTime(String(next ?? ""), time))}
						/>
						<CCField
							id={`${id}-time`}
							label="Time"
							type="time"
							fullWidth
							className={`em-time-field${time ? "" : " em-time-field--empty"}`}
							value={time}
							onChange={(e) => onChange(joinDateTime(date, e.target.value))}
						/>
					</div>
					{error && (
						<p className="em-req-datetime__error" role="alert">
							{error}
						</p>
					)}
				</fieldset>
			);
		}

		default:
			return (
				<CCField
					id={id}
					label={label}
					fullWidth
					multiline={field.type === "paragraph"}
					rows={field.type === "paragraph" ? 3 : undefined}
					hint={field.hint || undefined}
					error={error}
					autoFocus={autoFocus}
					value={value}
					onChange={(e) => onChange(e.target.value)}
				/>
			);
	}
};
